<template>
  <div class="PassportDatePickerComponent">
    <div class="inputs">
      <v-text-field
        ref="$day"
        v-model="day"
        :placeholder="t('common.dates.day')"
        :name="`${id}-day`"
        :maxLength="DAY_FIELD_SIZE"
        :max="maxValidDay"
        :rules="dayValidations"
        :error="!!fieldValidationErrorMessage"
        :hide-details="true"
        @keydown="isNumberKey($event)"
        @change="onChange(setDay, $event.target.value)"
        @input="inputDay($event.target.value)"
      />

      <v-text-field
        ref="$month"
        v-model="month"
        :placeholder="t('common.dates.month')"
        :name="`${id}-month`"
        :maxLength="MONTH_FIELD_SIZE"
        :max="maxValidMonth"
        :rules="monthValidations"
        :error="!!fieldValidationErrorMessage"
        :hide-details="true"
        @keydown="isNumberKey"
        @change="onChange(setMonth, $event.target.value)"
        @input="inputMonth($event.target.value)"
      />

      <v-text-field
        ref="$year"
        v-model="year"
        :placeholder="t('common.dates.year')"
        :name="`${id}-year`"
        :maxLength="YEAR_FIELD_SIZE"
        :max="minValidYear"
        :rules="yearValidations"
        :error="!!fieldValidationErrorMessage"
        :hide-details="true"
        @keydown="isNumberKey"
        @change="onChange(setYear, $event.target.value)"
      />
    </div>

    <v-text-field
      ref="$date"
      :id="`${id}-full-date`"
      class="full-date"
      v-model="inputDateFormat"
      :rules="dateValidations"
      :hide-details="true"
    />

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { useI18n } from 'vue-i18n'
  import { maxDaysByMonth, areEquals } from '@/utils/date.utils.js'
  import { validDate } from '@/utils/validations.utils'
  import { addNecessaryLeadingZeros } from '@/utils/textUtils'
  import ErrorMessageComponent from './ErrorMessageComponent.vue'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { isNumberKey } from '@/utils/input.utils'
  const DEFAULT_MAX_DAYS = 31
  const DEFAULT_MAX_MONTH = 12
  const DAY_FIELD_SIZE = 2
  const MONTH_FIELD_SIZE = 2
  const YEAR_FIELD_SIZE = 4

  const { t } = useI18n()
  const { maxValue, isPositiveInteger } = useRules()

  const emit = defineEmits(['update:modelValue'])

  interface PropsInterface {
    id: string
    modelValue: Date | null
    rules: ValidationRuleType[]
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    modelValue: null,
    rules: () => []
  })

  const $date = ref<any>(null)
  const $day = ref<any>(null)
  const $month = ref<any>(null)
  const $year = ref<any>(null)

  const day = ref<string>('')
  const month = ref<string>('')
  const year = ref<string>('')

  const validationRules = {
    maxDay: (value: string) => maxValue(value, maxValidDay.value, 'common.validations.date'),
    maxMonth: (value: string) => maxValue(value, 12, 'common.validations.date'),
    validDate: (value: string) =>
      value === '//'
        ? t('common.validations.date-empty')
        : validDate(value) || t('common.validations.date')
  }

  const dayValidations = [validationRules.maxDay, isPositiveInteger]
  const monthValidations = [validationRules.maxMonth, isPositiveInteger]
  const yearValidations = [isPositiveInteger]
  const dateValidations = [validationRules.validDate, ...props.rules]

  const fieldValidationErrorMessage = computed(() => {
    if (!$day?.value || !$month?.value || !$year?.value) return ''

    return (
      $date.value?.errorMessages[0] ||
      $day.value?.errorMessages[0] ||
      $month.value?.errorMessages[0] ||
      $year.value?.errorMessages[0] ||
      ''
    )
  })

  const inputDateFormat = computed(() => `${day.value}/${month.value}/${year.value}`)

  const date = computed(() => {
    if (!isValidDate()) {
      return null
    }
    const monthNew = Number(month.value)
    const dayNew = Number(day.value)

    return new Date(
      `${year.value}/${monthNew < 10 ? '0' + monthNew : monthNew}/${
        dayNew < 10 ? '0' + dayNew : dayNew
      }`
    )
  })

  const maxValidDay = computed(() => {
    if (!month.value || !year.value) {
      return DEFAULT_MAX_DAYS
    }

    return maxDaysByMonth(Number(month.value), Number(year.value))
  })

  const maxValidMonth = computed(() => {
    if (!year.value || Number(year.value) > Number(minValidYear.value)) {
      return DEFAULT_MAX_MONTH
    }

    const currentMonth = new Date().getMonth() + 1
    return currentMonth
  })

  const minValidYear = computed(() => {
    return new Date().getFullYear()
  })

  watch(
    () => props.modelValue,
    (value) => {
      setDay(String(value?.getDate?.() ?? ''))
      setYear(String(value?.getFullYear?.() ?? ''))

      const month = value?.getMonth?.()
      setMonth(String(month || month === 0 ? month + 1 : ''))
    },
    { immediate: true }
  )

  function onChange(setterCallback: (value: string) => void, value: string) {
    setterCallback(value)

    if (day.value && month.value && year.value && year.value.length === 4 && isValidDate()) {
      emitDate()
    }
  }

  function inputDay(value: string) {
    if (runValidation(dayValidations, value) && value.length > 1) {
      $month.value.focus()
    }
  }

  function setDay(value: string) {
    if (!runValidation(dayValidations, value) || !value.length) {
      return
    }

    day.value = addNecessaryLeadingZeros(value, DAY_FIELD_SIZE)
  }

  function inputMonth(value: string) {
    //if(value === "1") return;
    if (runValidation(monthValidations, value) && value.length > 1) {
      $year.value.focus()
    }
  }

  function setMonth(value: string) {
    if (!runValidation(monthValidations, value) || !value.length) {
      return
    }

    month.value = addNecessaryLeadingZeros(value, MONTH_FIELD_SIZE)
  }

  function setYear(value: string) {
    if (!runValidation(yearValidations, value)) {
      return
    }

    year.value = addNecessaryLeadingZeros(value, YEAR_FIELD_SIZE)
  }

  function isValidDate() {
    return (
      runValidation(dayValidations, day.value) &&
      runValidation(monthValidations, month.value) &&
      runValidation(yearValidations, year.value) &&
      runValidation(dateValidations, `${day.value}/${month.value}/${year.value}`)
    )
  }

  // todo - use as util
  function runValidation(rules: ValidationRuleType[], value: any): boolean {
    return rules.every((rule) => rule(value) === true)
  }

  function isInvalidDateObject(date: Date | null) {
    return String(date) === 'Invalid Date'
  }

  function emitDate() {
    if (areEquals(date.value, props.modelValue)) {
      return
    }

    const dateToEmit = isInvalidDateObject(date.value) ? null : date.value
    emit('update:modelValue', dateToEmit)
  }
</script>

<style lang="scss" scoped>
  .PassportDatePickerComponent {
    .inputs {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: $gap-xl;

      :deep(.v-field) {
        background-color: $color-grey-200;
        border-radius: $b-radius-0;
        box-shadow: $box-shadow-0;
        transition: all 0.2s ease-in-out;

        .v-field__input {
          font-family: $font-family-primary;
          color: $color-neutral-dark;

          &::placeholder {
            color: $color-neutral;
            opacity: 1;
          }
        }

        /*       &:focus-within {
        box-shadow: $box-shadow-focus;
      } */
      }

      :deep(.v-field--error) {
        background-color: $color-error-200;

        .v-field__input {
          &::placeholder {
            color: $color-neutral;
            opacity: 1;
          }
        }
      }
    }

    .full-date {
      // display: none;
      visibility: hidden;
      height: 0;
    }
  }
</style>
